import { MappingProblemStatus, MappingProblemType } from '@/constants';
import { dateFormatInUserTimeZone } from '@/helpers';
import { AbstractModel } from '@/models/abstractModel';
import { MappingProblemSolutionItem } from '@/models/mappingProblemSolution';
import type { IMappingProblemEventFixRaw, IMappingProblemExtra, IMappingProblemRow } from '@/types/mappingProblem';

function parseJsonObject(raw: string): Record<string, any> | null {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

function splitIds(raw: string): string[] {
    if (!raw) return [];
    return raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
}

export class MappingProblemItem extends AbstractModel {
    public additionalInfo: IMappingProblemExtra;
    public autoFixError: string | null;
    public canAutoFix: 0 | 1;
    public createdAt: string;
    public customEventIds: string[];
    public description: string;
    public eventFixes: IMappingProblemEventFixRaw[];
    public eventIds: string[];
    public eventStartedAt: string;
    public id: number;
    public problemBookmakerIds: string[];
    public processingStartedAt: string | null;
    public resolvedAt: string | null;
    public solutions: MappingProblemSolutionItem[];
    public statusId: MappingProblemStatus;
    public typeId: MappingProblemType;
    public updatedAt: string;

    constructor(data: IMappingProblemRow) {
        super();
        this.id = data.id;
        this.description = data.description;
        this.eventStartedAt = data.event_started_at;
        this.typeId = data.type_id;
        this.statusId = data.status_id;
        this.createdAt = data.created_at;
        this.updatedAt = data.updated_at;
        this.processingStartedAt = data.processing_started_at;
        this.resolvedAt = data.resolved_at;
        this.customEventIds = splitIds(data.custom_event_ids);
        this.eventIds = splitIds(data.event_ids);
        this.problemBookmakerIds = splitIds(data.problem_bookmaker_ids);
        this.additionalInfo = parseJsonObject(data.extra) as IMappingProblemExtra;
        this.autoFixError = data.auto_fix_error;
        this.canAutoFix = data.can_auto_fix;
        this.solutions = data.solutions.map((raw) => new MappingProblemSolutionItem(raw));
        this.eventFixes = data.event_fixes ?? [];
    }

    get isAwaitingData(): boolean {
        return this.statusId === MappingProblemStatus.AwaitingData;
    }

    get isResolved(): boolean {
        return (
            this.statusId === MappingProblemStatus.ResolvedManual ||
            this.statusId === MappingProblemStatus.ResolvedThirdParty ||
            this.statusId === MappingProblemStatus.ResolvedAutomatic
        );
    }

    formatEventStartedAt(separator = '\n'): string {
        return this.eventStartedAt
            .split(',')
            .map((date) => date.trim())
            .filter(Boolean)
            .map((date) => dateFormatInUserTimeZone(date, 'dd-MM-yyyy HH:mm:ss'))
            .join(separator);
    }
}
